import React, { memo, ReactElement, useRef, useEffect } from 'react';
import throttle from 'lodash/throttle';
import classNames from 'classnames';
import Spinner, { Size } from '@atlaskit/spinner';
import Button from '@atlaskit/button/standard-button';
import EmptyState from '@atlaskit/empty-state';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import Pagination from '@atlaskit/pagination';
import { ApolloError } from '@apollo/client';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { TableLoader } from '@css-xp/ppc-page-loaders';
import { CustomPagination } from '../../custom-pagination';
import { useFeatureToggle } from '../../hooks';
import './custom-dynamic-result-table.scss';

export interface TableProps {
  children: React.ReactNode;
  noResults: boolean;
  isLoading: boolean;
  error: string | null | undefined | ApolloError;
  heading?: React.ReactNode;
  selectableResults?: boolean;
  testId?: string;
  loadingSpinnerSize?: Size;
  emptyStateProps: React.ComponentProps<typeof EmptyState>;
  errorStateProps?: {
    header: string;
    description: string | ReactElement;
  };
  className?: string;
  filters?: React.ReactNode;
  actions?: React.ElementType<any>[];
  isPaginationEnabled?: boolean;
  totalCount?: number;
  rowsPerPage?: number;
  currentPage?: number;
  onPageChange?: (page: number) => unknown;
  onRowChange?: (rowsPerPage: number) => unknown;
  boxShadow?: boolean;
  customiseColumnView?: React.ReactElement;
  shouldForceEnableFilters?: boolean;
  /* hasTableWrapper is needed when making horizontally scrollable table */
  hasTableWrapper?: boolean;
  showLoadMore?: boolean;
  loadMoreText?: string;
  onLoadMore?: () => unknown;
  loadMoreLoading?: boolean;
  innerHeading?: React.ReactNode;
  shouldShowHorizontalScrollAnimation?: boolean;
  isSearched?: boolean;
  isAnyFilterApplied?: boolean;
  beforeTable?: React.ReactElement | null;
  actionsAfterFilter?: React.ReactElement | null;
  loaderOptions?: {
    filtersCount?: number;
    hideSearchBar?: boolean;
    hideFilters?: boolean;
    hideActions?: boolean;
    columnsCount?: number;
    shouldRightAlignLastCol?: boolean;
    columnsWidth?: string;
  };
  customLoader?: React.ReactNode;
}

const handleSelfPaginated = (onPageChange: any, currentPage = 1, totalCount?: number, rowsPerPage?: number) => {
  if (totalCount && rowsPerPage && totalCount <= rowsPerPage) {
    return null;
  }
  const totalPages =
    totalCount && rowsPerPage ? [...Array(Math.ceil(totalCount / rowsPerPage)).keys()].map((num) => num + 1) : [1];
  return (
    <Pagination
      pages={totalPages}
      onChange={(_e, pageNumber) => onPageChange(pageNumber)}
      defaultSelectedIndex={currentPage - 1}
      testId="controlled-pagination"
    />
  );
};
export const renderTableActions = (actions: React.ElementType<any>[]) => {
  return (
    <div className="actions-container" data-testid="actions-container">
      {actions.map((Action, index) => {
        return <Action key={`action-${index}`} />;
      })}
    </div>
  );
};

const scrollLeftValue = 20;

// False warning: https://github.com/yannickcr/eslint-plugin-react/issues/2105
// eslint-disable-next-line react/display-name
export const CustomDynamicResultTable = memo(
  ({
    children,
    noResults,
    emptyStateProps,
    errorStateProps,
    isLoading,
    error,
    heading,
    testId,
    selectableResults = true,
    loadingSpinnerSize = 'large',
    className,
    filters,
    actions,
    isPaginationEnabled = false,
    totalCount,
    rowsPerPage,
    currentPage,
    onPageChange,
    onRowChange,
    boxShadow = true,
    customiseColumnView,
    shouldForceEnableFilters = false,
    hasTableWrapper = false,
    showLoadMore = false,
    onLoadMore,
    loadMoreLoading = false,
    loadMoreText = 'Load More',
    innerHeading,
    shouldShowHorizontalScrollAnimation = false,
    isSearched,
    isAnyFilterApplied,
    beforeTable = null,
    loaderOptions,
    actionsAfterFilter = null,
    customLoader,
  }: TableProps) => {
    const [isEnabled] = useFeatureToggle();
    const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
    // Check if there are no results and no search filter is applied
    const emptySearchAndEmptyFiltersWithNoResult = !isLoading && noResults && !isSearched && !isAnyFilterApplied;
    // Check if DESIGN_ITERATION_1 feature flag is enabled and no results found
    const isDesignIteration1EnabledAndNoResult = isDesignIteration1Enabled && noResults;
    // Determine whether to display action in the emptyState container. Action is not shown when DESIGN_ITERATION_1 is enabled and no results
    const shouldShowAction = actions && actions.length > 0 && !isDesignIteration1EnabledAndNoResult;
    // Decide if filter container should be shown. It is hidden when DESIGN_ITERATION_1 is enabled and search results are empty
    const shouldShowFilterContainer =
      !loaderOptions?.hideFilters &&
      !(isDesignIteration1Enabled && emptySearchAndEmptyFiltersWithNoResult && !isLoading);

    const tableContainerRef = useRef<HTMLDivElement>(null);
    // Using ref to keep the isScrollAnimationDoneRef value intact on subsequent
    // renders to avoid triggering the animation again.
    const isScrollAnimationDoneRef = useRef<boolean>(false);
    const currentRef = tableContainerRef.current;
    const classForScrollOverflowIndicator = 'with-horizontal-scroll-overflow-indicator';
    useEffect(() => {
      let scrollLeft = 0;
      /* istanbul ignore next */
      // This is a code that is usually tested in integration tests
      const handleHorizontalScroll = () => {
        if (currentRef) {
          throttle(function () {
            if (scrollLeft !== currentRef.scrollLeft) {
              // horizontally scrolled case
              scrollLeft = currentRef.scrollLeft;
              if (scrollLeft === 0) {
                currentRef.classList.remove(classForScrollOverflowIndicator);
              } else {
                currentRef.classList.add(classForScrollOverflowIndicator);
              }
            }
          }, 300)();
        }
      };

      currentRef?.addEventListener('scroll', handleHorizontalScroll);
      return () => {
        // Remove scroll event listener on unmount
        currentRef?.removeEventListener('scroll', handleHorizontalScroll);
      };
    }, [currentRef]);

    const scrollLeft = (leftValue: number) => {
      if (currentRef) {
        currentRef.scrollTo({
          left: leftValue,
          behavior: 'smooth',
        });
      }
    };

    const triggerHorizontalScrollAnimation = () => {
      isScrollAnimationDoneRef.current = true;

      scrollLeft(scrollLeftValue);

      setTimeout(() => {
        scrollLeft(-scrollLeftValue);
      }, 300);
      setTimeout(() => {
        scrollLeft(scrollLeftValue);
      }, 600);
      setTimeout(() => {
        scrollLeft(-scrollLeftValue);
      }, 900);
    };

    const dataTestId = 'custom-dynamic-result-table' + (testId ? `-${testId}` : '');
    let noDataView;

    if (isLoading) {
      if (customLoader) {
        noDataView = customLoader;
      } else if (isDesignIteration1Enabled && !customLoader) {
        noDataView = (
          <TableLoader
            hideSearchBar={loaderOptions?.hideSearchBar ?? shouldShowFilterContainer}
            hideFilters={loaderOptions?.hideFilters ?? shouldShowFilterContainer}
            hideActions={loaderOptions?.hideActions ?? shouldShowFilterContainer}
            testId={`${dataTestId}-loading`}
            filtersCount={loaderOptions?.filtersCount}
            columnsCount={loaderOptions?.columnsCount}
            shouldRightAlignLastCol={loaderOptions?.shouldRightAlignLastCol}
            columnsWidth={loaderOptions?.columnsWidth}
          />
        );
      } else {
        noDataView = (
          <div
            className={classNames('no-data-view pad-vertical', {
              'loading-overlay': !noResults,
            })}
          >
            <Spinner testId={`${dataTestId}-loading`} size={loadingSpinnerSize} />
          </div>
        );
      }
    } else if (error && noResults) {
      noDataView = (
        <div className="no-data-view error-view pad-vertical" data-testid={`${dataTestId}-error`}>
          {errorStateProps ? (
            <EmptyState {...errorStateProps} />
          ) : (
            <>
              <CrossCircleIcon label="Error" size="small" />
              <span className="margin-left">Oh no! Something went wrong, please try again</span>
              <span className="error">({error?.toString()})</span>
            </>
          )}
        </div>
      );
    } else if (noResults) {
      noDataView = (
        <div className="no-data-view" data-testid={`${dataTestId}-no-results`}>
          <EmptyState {...emptyStateProps} />
        </div>
      );
    }

    const renderTable = () => {
      return (
        <table
          className={classNames(`aui table-list table-search-results no-sticky-header`, {
            'selectable-results': selectableResults,
            'loading-state': isLoading && !noResults,
            'no-filters': !shouldShowFilterContainer,
          })}
        >
          {/* this is required to rerender children when the children change and 
          children should not show when table is loading for new design */}
          {isDesignIteration1Enabled && isLoading ? null : children}
        </table>
      );
    };

    const shouldAnimate = shouldShowHorizontalScrollAnimation && !isScrollAnimationDoneRef.current && !isLoading;
    // https://hello.atlassian.net/wiki/spaces/~495238681/pages/2645225804/Update+Improvements+in+horizontal+scrolling+behaviour+in+CustomDynamicResultTable
    shouldAnimate && triggerHorizontalScrollAnimation();
    return (
      <div data-testid={dataTestId}>
        {heading ? typeof heading === 'string' ? <h4>{heading}</h4> : heading : null}
        <div
          className={classNames('custom-dynamic-result-table', className, {
            'box-shadow': boxShadow && !isDesignIteration1Enabled,
            'custom-dynamic-result-table-no-results': noResults,
            'custom-dynamic-result-table-v2': isDesignIteration1Enabled,
          })}
        >
          <div
            className={classNames({
              'position-relative': isLoading && !noResults,
            })}
          >
            {innerHeading ? innerHeading : null}
            {shouldShowAction && renderTableActions(actions)}
            {beforeTable}
            {shouldShowFilterContainer && (
              <div className="custom-dynamic-result-table-filter-container">
                {/* showFilter */}
                {shouldForceEnableFilters && filters && (
                  <div
                    data-testid="custom-dynamic-result-table-filters"
                    className="custom-dynamic-result-table-filters"
                  >
                    {filters}
                  </div>
                )}
                {customiseColumnView && !isDesignIteration1EnabledAndNoResult ? (
                  <div className="customise-column-view">{customiseColumnView}</div>
                ) : null}
              </div>
            )}
            {actionsAfterFilter}
            {hasTableWrapper && !noResults ? (
              // Table wrapper is needed when making horizontally scrollable table
              <div className="custom-table-container" data-testid="custom-table-container" ref={tableContainerRef}>
                {renderTable()}
              </div>
            ) : (
              renderTable()
            )}
            {noDataView}
          </div>
          {!noDataView && !isLoading && isPaginationEnabled && (
            <div className="controlled-pagination-wrapper">
              <CustomPagination
                rowCount={rowsPerPage}
                totalCount={totalCount}
                onChangeRow={onRowChange}
                currentPage={currentPage}
                selfPaginatedElement={handleSelfPaginated(onPageChange, currentPage, totalCount, rowsPerPage)}
              />
            </div>
          )}
          {showLoadMore && (
            <div className="load-more-button-wrapper">
              <Button
                className={'load-more-button'}
                testId={`${dataTestId}-load-more`}
                onClick={onLoadMore ? onLoadMore : undefined}
              >
                {loadMoreText}
              </Button>
            </div>
          )}
          {loadMoreLoading && (
            <div className={classNames('no-data-view pad-vertical')}>
              <Spinner testId={`${dataTestId}-load-more-loading`} size={loadingSpinnerSize} />
            </div>
          )}
        </div>
      </div>
    );
  }
);
